<template>
  <div>
    <v-alert v-if='alert' type="error">Cannot lock batch unless total is 0</v-alert>
    <wc-page-title title='Batch Detail'>
      <template v-slot:rightSide v-if='batch'>
        <v-icon v-if='lockEdits' :color="lockEdits ? 'red' : 'grey'">mdi-lock</v-icon>
        <wc-burger-menu>
          <v-list-item @click='toggleLock'>
            <v-list-item-icon>
              <v-icon color='red'>mdi-lock</v-icon>
            </v-list-item-icon>
            <v-list-item-title v-if='batch.is_locked'>UN-Lock Batch</v-list-item-title>
            <v-list-item-title v-else>Lock Batch</v-list-item-title>
          </v-list-item>
        </wc-burger-menu>
      </template>
    </wc-page-title>
    <v-container v-if='batch'>
      <v-alert v-if='batch.reconciliation' small type='error' border='left'>Batch has been reconciled and cannot be
        edited.</v-alert>
      <v-row>
        <v-col cols='12' md='3'>
          <wc-data-box :showEdit='!lockEdits' @edit='editBasics' title='Basics'
            :titleIcon='$store.state.icons.dataBox.basics.icon'>
            <wc-two-column-table :items='basicsData'></wc-two-column-table>
          </wc-data-box>
        </v-col>
        <v-col cols='12' md='3'>
          <wc-data-box hideToolbar v-if='batch'>
            <v-row no-gutters class='mt-3'>
              <v-col class='text-center'>
                <span class='title'>{{ pmtsTotal | decMoney }}</span><br>
                <small class='grey--text'>Total pmts</small>
              </v-col>
              <v-col class='text-center'>
                <span :class="diffToBatch === 0 ? 'title success--text' : 'title error--text'">{{ diffToBatch |
                  decAccounting }}</span><br>
                <small class='grey--text'>Diff</small>
              </v-col>
            </v-row>
          </wc-data-box>
        </v-col>
        <v-col cols='12' md='6'>
          <wc-data-box title='Invoice Lookup' :titleIcon='toolbarData.icon' :titleIconColor='toolbarData.iconColor'
            showHelp :helpText='helpText'>
            <v-card-text>
              <invoice-lookup @newResults='loadNewResults' ref='invoiceLookup' :disable='lockEdits'>
              </invoice-lookup>
            </v-card-text>
          </wc-data-box>
        </v-col>
      </v-row>

      <br><br><br>

      <v-row>
        <v-col cols='12' md='6'>
          <wc-data-box title='Create Payment' titleIcon='mdi-credit-card' :loadingButtons='postingPmt'>
            <v-card-text>
              <v-form v-model='isFormValid' :disabled='postingPmt || lockEdits' ref='form'>
                <v-row>
                  <v-col cols='12' md='6'>
                    <wc-datepicker required label='Payment date' v-model='newPmt.date_paid'>
                    </wc-datepicker>
                  </v-col>
                  <v-col cols='12' md='6'>
                    <v-text-field :rules='[rules.decimal]' label='Amount paid' v-model='newPmt.amount' reverse
                      prepend-icon='mdi-currency-usd'></v-text-field>
                  </v-col>
                  <v-col cols='12' class='text-right'>
                    <v-btn depressed color='success' small @click='postPmt' :disabled='!isFormValid || !selectedInvoice'
                      :loading='postingPmt'>
                      <v-icon left small>mdi-checkbox-marked-circle-outline</v-icon>
                      Post payment
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </wc-data-box>
          <br>
          <wc-data-box title='Payments Recorded in Batch'>
            <v-card-text v-if='pmts.length === 0'>
              No payments posted yet.
            </v-card-text>
            <v-simple-table dense>
              <thead class='overline'>
                <tr>
                  <th>Bill to</th>
                  <th>Invoice</th>
                  <th class='text-right'>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for='(p, index) in pmts' :key='index' @click='openInvoice(p.invoice)'>
                  <td>{{ p.invoice_bill_to }}</td>
                  <td>{{ p.invoice_obj_number }}</td>
                  <td class='text-right'>{{ p.amount | dec }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </wc-data-box>
        </v-col>
        <v-col cols='12' md='6'>
          <wc-data-box title='Invoice Details and Payment'>
            <wc-two-column-table :items='invoiceData'></wc-two-column-table>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn :disabled='results.length === 0 || !selectedInvoice || lockEdits' small text color='info'
                @click='openInvoice(selectedInvoice.id)'>
                Open Invoice
                <v-icon small right>mdi-open-in-new</v-icon>
              </v-btn>
            </v-card-actions>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Project Name</th>
                  <th>Bill To</th>
                  <th>Amount OS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for='r in results' :key='r.id' @click='selectInvoice(r)' :class='r.class'>
                  <td>{{ r.number }}</td>
                  <td v-if='r.project_data !== null && r.project_data !== undefined'> {{ r.project_data.name }}</td>
                  <td>{{ r.bill_to_display }}</td>
                  <td>{{ r.amount_os | decMoney }}</td>
                </tr>
                <tr v-if='results.length == 0'>
                  <td colspan='3'>No invoices</td>
                </tr>
              </tbody>
            </v-simple-table>
          </wc-data-box>
        </v-col>
      </v-row>
    </v-container>

    <wc-dialog :showDialog='showEditDialog' title='Edit basics' :icon='$store.state.icons.dataBox.basics.icon'
      width='350' :disableButtons='!isBasicsValid' :loadingButtons='isSavingBasics' @cancel='showEditDialog = false'
      @save='updateBasics'>
      <v-form v-model='isBasicsValid' ref='basicsForm'>
        <wc-datepicker required v-model='batchCopy.date' label='Batch Date'></wc-datepicker>
        <v-text-field append-icon='mdi-currency-usd' reverse :rules='[rules.decimal]' label='Batch total'
          v-model='batchCopy.amount'></v-text-field>
      </v-form>
    </wc-dialog>
    <deposit-tracker v-if='batch' :deposit='batch' ref='actionTracker'></deposit-tracker>
  </div>
</template>

<script>
import DepositTracker from '@/components/trackers/DepositTracker';
import InvoiceLookup from '@/modules/accounting/components/InvoiceLookup';
import accountingAPI from '@/api/accounting';
import trackerAPI from '@/api/tracker';
import miscServices from '@/services/misc';
import formRules from '@/mixins/formRules';
import moment from 'moment';
import _ from 'lodash';

export default {
  name: 'all-deposits',
  mixins: [formRules],
  data: () => ({
    alert: false,
    isLoading: true,
    isSaving: false,
    isSavingBasics: false,
    isFormValid: false,
    isBasicsValid: false,
    postingPmt: false,
    selectedInvoice: null,
    showEditDialog: false,
    resetObj: {
      invoice: '',
      date_paid: '',
      account: null,
      payment_method: 'Q',
      amount: 0
    },
    newPmt: {},
    batch: null,
    batchCopy: {},
    results: [],
    pmts: [],
    helpText: `
      Search for invoices by number, first name, last name, or balance Outstanding. Results will
      be populated in the area below the "Search Results" box. Click on a row in the bottom half to select an invoice
      and its details will be presented in the top half.<br>
      <br>

      Selecting an invoice will automatically populate the "Create Payment" box with 
      Amount Paid equal to the amount outstanding for that invoice. Click "Post Payment" to appliy a payment to
      this invoice.<br>
      <br>
      The payments recorded will be shown in the table below the "Create Payment" box. You can click on any of posted
      payments to open the related invoice in a new window.
    `
  }),
  computed: {
    toolbarData() {
      return this.$store.state.icons.dataBox.deposits
    },
    lockEdits() {
      return this.batch.is_locked || this.batch.reconciliation !== null;
    },
    basicsData() {
      if (this.batch) {
        return [
          { text: 'Batch date', value: this.$options.filters.shortDate(this.batch.date) },
          { text: 'Batch total', value: this.$options.filters.decMoney(this.batch.amount) },
          { text: '# Payments', value: this.pmts.length }
        ]
      }
      return []
    },
    pmtsTotal() {
      return _.round(_.sumBy(this.pmts, 'amount'), 2)
    },
    canLock() {
      return this.diffToBatch == 0
    },
    diffToBatch() {
      return _.round(_.subtract(this.pmtsTotal, this.batch.amount), 2)
    },
    invoiceData() {
      if (this.selectedInvoice && this.results.length > 0) {
        return [
          { text: 'Bill to', value: this.selectedInvoice.bill_to_obj ? this.selectedInvoice.bill_to_obj.name_display : 'No bill to?' },
          { text: 'Number', value: this.selectedInvoice ? this.selectedInvoice.number : '' },
          { text: 'Invoice date', value: this.selectedInvoice ? this.$options.filters.shortDate(this.selectedInvoice.invoice_date) : '' },
          { text: 'Due date', value: this.selectedInvoice ? this.$options.filters.shortDate(this.selectedInvoice.date_due) : '' },
          { text: 'Amount Outstanding', value: this.selectedInvoice ? this.$options.filters.decMoney(this.selectedInvoice.amount_os) : '' }
        ]
      }
      return [
        { text: 'Bill to', value: '' },
        { text: 'Number', value: '' },
        { text: 'Invoice date', value: '' },
        { text: 'Due date', value: '' },
        { text: 'Amount Outstanding', value: '' }
      ]
    }
  },
  mounted() {
    this.getData()
  },
  methods: {
    async getData() {
      this.isLoading = true
      this.batch = await accountingAPI.getDeposit(this.$route.params.id)
      this.pmts = await accountingAPI.getPaymentsForDeposit(this.$route.params.id)
      this.resetPmt()
      this.isLoading = false
    },
    resetPmt() {
      this.newPmt = _.cloneDeep(this.resetObj)
      this.newPmt.date_paid = this.batch ? this.batch.date : ''
      this.newPmt.payment_method = 'V'
      this.newPmt.account = this.batch.acctount
      this.newPmt.deposit = this.batch.id
    },
    editBasics() {
      this.batchCopy = _.cloneDeep(this.batch);
      this.showEditDialog = true;
    },
    async updateBasics() {
      this.isSavingBasics = true;
      this.batch = await accountingAPI.updateDeposit(this.batchCopy.id, this.batchCopy);
      this.showEditDialog = false;
      this.isSavingBasics = false;
    },
    loadNewResults(results) {
      this.results = results
    },
    openInvoice(invID) {
      const link = document.createElement('a')
      const invURL = this.$router.resolve({ name: 'InvoiceOverview', params: { id: invID } })
      link.target = '_blank'
      link.href = invURL.href
      link.click()
    },
    async toggleLock() {
      const total = this.pmtsTotal - this.batch.amount;
      if (total !== 0) {
        this.alert = true;
        return;
      }

      this.isLoading = true;
      let desc = '';
      if (this.batch.is_locked) {
        desc = 'Batch UN-locked.'
        this.batch.is_locked = false
        this.batch.deposited_by = null
      } else {
        desc = 'Batch locked.'
        this.batch.is_locked = true
        this.batch.deposited_by = this.$store.state.auth.profile.id
      }

      this.batch = await accountingAPI.updateDeposit(this.batch.id, this.batch);

      const tracker_data = {
        'description': desc,
        'taken_by': this.$store.state.auth.profile.id,
        'action_date': moment().format('YYYY-MM-DD').toString()
      }
      const tracker = {
        'tracker_data': tracker_data,
        'deposit': this.batch.id
      }
      const res = await trackerAPI.createDepositTracker(tracker);
      this.$refs.actionTracker.pushItem(res);
      this.isLoading = false;
    },
    async selectInvoice(row) {
      this.results = this.results.map(function (el) {
        let o = Object.assign({}, el)
        o.class = ''
        return o
      })
      row.class = 'info'
      this.results = miscServices.replaceObject(this.results, row)
      this.loadingInvoice = true
      this.selectedInvoice = await accountingAPI.getInvoice(row.id)
      this.newPmt.invoice = this.selectedInvoice.id
      //      this.newPmt.date_paid = moment().format('YYYY-MM-DD').toString()
      this.newPmt.amount = this.selectedInvoice.amount_os
    },
    async postPmt() {
      this.alert = false;
      this.postingPmt = true;
      let tmp = await accountingAPI.createInvoicePayment(this.newPmt);
      let inv = _.cloneDeep(tmp.invoice_obj);

      inv.amount_os -= tmp.amount;

      accountingAPI.updateInvoice(inv.id, inv);
      const tracker = {
        'tracker_data': {
          'description': 'Recorded payment against invoice #' + this.selectedInvoice.number + ' of ' + this.newPmt.amount,
          'taken_by': this.$store.state.auth.profile.id,
          'action_date': moment().format('YYYY-MM-DD').toString(),
        },
        'deposit': this.batch.id
      }

      const res = await trackerAPI.createDepositTracker(tracker);
      const invoice_tracker = {
        'invoice': this.selectedInvoice.id,
        'tracker': res.tracker
      }
      trackerAPI.createInvoiceTracker(invoice_tracker);

      this.$refs.actionTracker.pushItem(res);
      this.pmts.push(this.formatNewPayment(tmp))
      this.selectedInvoice = null
      this.$refs.invoiceLookup.clearSearch()
      this.results = []
      this.resetPmt()
      this.$refs.form.resetValidation()
      this.postingPmt = false
    },
    formatNewPayment(pmt) {
      let tmp = {
        id: pmt.id,
        payment_method_display: pmt.payment_method_display,
        invoice_obj_number: pmt.invoice_obj.number,
        date_paid: pmt.date,
        payment_method: pmt.payment_method,
        amount: pmt.amount,
        invoice: pmt.invoice,
        account: pmt.account,
        deposit: pmt.deposit
      }
      if (pmt.invoice.bill_to) {
        tmp.invoice_bill_to = pmt.invoice_obj.bill_to_obj.name_display
      } else {
        tmp.invoice_bill_to = 'No bill to?'
      }
      return tmp
    }
  },
  components: {
    'invoice-lookup': InvoiceLookup,
    'deposit-tracker': DepositTracker
  }
}
</script>
